// ─── Achats natifs (RevenueCat) ───────────────────────────────────
// StoreKit (iOS) / Google Play Billing (Android) via `react-native-purchases`
// — obligatoire pour un abonnement numérique consommé DANS l'app (politique
// Apple 3.1.1 / Google Play Billing). Le web garde Stripe (`paiementService.js`) :
// les règles des stores ne s'appliquent qu'aux binaires qu'ils distribuent.
//
// `require` protégé comme AdMob (`pub/PubZone.js`) : un binaire pas encore
// migré (module natif absent) ne doit jamais planter au chargement du bundle —
// il retombe simplement sur Stripe (branchement fait dans `SectionMonForfait`).
import { Platform } from 'react-native';

let Purchases = null;
if (Platform.OS !== 'web') {
  try { Purchases = require('react-native-purchases').default; } catch { /* module absent : app pas encore migrée */ }
}

export const achatsNatifsDisponibles = () => !!Purchases;

let dejaConfigure = false;

// À appeler une fois au démarrage de l'app (indépendamment de la connexion) —
// idempotent, sans effet si le module est absent ou la clé non renseignée.
// UNE CLÉ PAR PLATEFORME (24/08) : RevenueCat en délivre une par "App" connectée
// (App Store ↔ appl_..., Google Play ↔ goog_...) — plus de clé unique partagée
// une fois sorti du Test Store (qui, lui, acceptait la même clé des deux côtés).
export function configurerAchatsNatifs() {
  if (!Purchases || dejaConfigure) return;
  const apiKey = Platform.select({
    ios: process.env.EXPO_PUBLIC_REVENUECAT_IOS_API_KEY,
    android: process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY,
  });
  if (!apiKey) return;
  Purchases.configure({ apiKey });
  dejaConfigure = true;
}

// À appeler quand `uid` devient connu (connexion) — associe l'identité RevenueCat
// à notre propre utilisateur, pour que le webhook sache quel `users/{uid}` maj.
export async function identifierUtilisateurNatif(uid) {
  if (!Purchases || !uid) return;
  try { await Purchases.logIn(uid); } catch { /* réessaiera au prochain appel */ }
}

export async function deconnecterUtilisateurNatif() {
  if (!Purchases) return;
  try { await Purchases.logOut(); } catch { /* pas grave : anonyme par défaut */ }
}

// Offres configurées côté RevenueCat (packages disponibles à l'achat).
export async function obtenirOfferings() {
  if (!Purchases) return null;
  try { return await Purchases.getOfferings(); } catch { return null; }
}

// Lance l'achat d'un package (issu de `obtenirOfferings()`), renvoie les infos
// client à jour (entitlements actifs) une fois la transaction validée.
export async function acheterPackage(pkg) {
  if (!Purchases) throw new Error('Achats natifs indisponibles sur cette plateforme.');
  const { customerInfo } = await Purchases.purchasePackage(pkg);
  return customerInfo;
}

export async function restaurerAchatsNatifs() {
  if (!Purchases) return null;
  try { return await Purchases.restorePurchases(); } catch { return null; }
}

// Résilier un abonnement natif ne peut PAS se faire par notre propre code —
// Apple/Google imposent de passer par leur écran natif de gestion
// d'abonnement (contrairement à Stripe où `annulerAbonnement()` suffit).
export async function gererAbonnementNatif() {
  if (!Purchases) return;
  try { await Purchases.showManageSubscriptions(); } catch { /* pas grave : l'utilisateur peut y aller depuis les réglages du store */ }
}

// Cherche, parmi une liste de clés d'entitlement (nos plans payants), celle qui
// est active pour l'utilisateur — ou `null` (aucune, ou module indisponible).
export async function planActifNatif(clesEntitlements) {
  if (!Purchases) return null;
  try {
    const info = await Purchases.getCustomerInfo();
    return clesEntitlements.find(cle => info.entitlements.active[cle] !== undefined) || null;
  } catch { return null; }
}
